import { formatDistanceToNow } from 'date-fns';
import type { ReactElement } from 'react';

import type { DeckPowerAssessment } from '@mtgcraft/shared';
import { COMMANDER_BRACKET_LABELS } from '@mtgcraft/shared';

import { AiChip } from '../components/AiChips';

export interface DeckPowerResultProps {
  assessment: DeckPowerAssessment;
  /** ISO date of a stored assessment; omit when showing a run that just finished */
  savedAt?: string | null;
  savedModel?: string | null;
}

const IMPACT_TONE = {
  raises: 'success',
  lowers: 'danger',
  neutral: 'neutral',
} as const;

const IMPACT_LABEL = {
  raises: 'Raises',
  lowers: 'Lowers',
  neutral: 'Context',
} as const;

const CONFIDENCE_TONE = {
  low: 'warning',
  medium: 'neutral',
  high: 'success',
} as const;

function PowerMeter({ value }: { value: number }): ReactElement {
  return (
    <div className="flex items-center gap-0.5" aria-hidden="true">
      {Array.from({ length: 10 }, (_, index) => (
        <span key={index} className={`h-6 w-2 rounded-sm ${index < value ? 'bg-aether' : 'bg-storm'}`} />
      ))}
    </div>
  );
}

export default function DeckPowerResult({ assessment, savedAt, savedModel }: DeckPowerResultProps): ReactElement {
  return (
    <div className="space-y-5">
      {savedAt && (
        <p className="text-ash text-xs">
          Saved assessment from {formatDistanceToNow(new Date(savedAt), { addSuffix: true })}
          {savedModel && <span className="font-mono"> · {savedModel}</span>}
        </p>
      )}

      <div className="border-storm bg-slate/40 flex flex-wrap items-center gap-6 rounded-lg border p-4">
        <div className="space-y-1.5">
          <span className="text-ash block text-xs tracking-wide uppercase">Power level</span>
          <div className="flex items-center gap-3">
            <span className="font-heading text-pearl text-3xl font-semibold tabular-nums">
              {assessment.power_level}
              <span className="text-ash text-lg">/10</span>
            </span>
            <PowerMeter value={assessment.power_level} />
          </div>
        </div>

        {assessment.bracket !== null && (
          <div className="space-y-1.5">
            <span className="text-ash block text-xs tracking-wide uppercase">Commander bracket</span>
            <p className="font-heading text-pearl text-3xl font-semibold">
              {assessment.bracket}
              <span className="text-silver ml-2 font-sans text-base font-normal">{COMMANDER_BRACKET_LABELS[assessment.bracket]}</span>
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <span className="text-ash block text-xs tracking-wide uppercase">Confidence</span>
          <div>
            <AiChip tone={CONFIDENCE_TONE[assessment.confidence]} className="capitalize">
              {assessment.confidence}
            </AiChip>
          </div>
        </div>
      </div>

      <p className="text-silver text-sm leading-relaxed">{assessment.summary}</p>

      {assessment.signals.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-ash text-xs font-medium tracking-wide uppercase">Evidence</h3>
          <ul className="space-y-2">
            {assessment.signals.map((signal) => (
              <li key={signal.label} className="border-storm bg-void/40 flex gap-3 rounded-lg border p-3">
                <AiChip tone={IMPACT_TONE[signal.impact]} className="mt-0.5 shrink-0">
                  {IMPACT_LABEL[signal.impact]}
                </AiChip>
                <div>
                  <p className="text-pearl text-sm font-medium">{signal.label}</p>
                  <p className="text-ash text-sm">{signal.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {assessment.key_cards.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-ash text-xs font-medium tracking-wide uppercase">Key cards</h3>
          <div className="flex flex-wrap gap-1.5">
            {assessment.key_cards.map((card) => (
              <AiChip key={card}>{card}</AiChip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
